import type { AttendanceSummary } from '@/types/calendar'
import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed', data)
  }

  return data
}

export interface CheckedInChild {
  id: string
  firstName: string
  lastName: string
}

export const attendanceApi = {
  // Get today's attendance summary
  async getToday(): Promise<AttendanceSummary> {
    return fetchWithAuth<AttendanceSummary>('/api/attendance/today')
  },

  // Get attendance for a specific date
  async getByDate(date: string): Promise<AttendanceSummary> {
    return fetchWithAuth<AttendanceSummary>(`/api/attendance/date/${date}`)
  },

  // Check in a child
  async checkIn(childId: string, notes?: string): Promise<{ success: boolean; checkedInAt: string }> {
    return fetchWithAuth<{ success: boolean; checkedInAt: string }>('/api/attendance/checkin', {
      method: 'POST',
      body: JSON.stringify({ childId, notes }),
    })
  },

  // Check out a child
  async checkOut(childId: string, notes?: string): Promise<{ success: boolean; checkedOutAt: string }> {
    return fetchWithAuth<{ success: boolean; checkedOutAt: string }>('/api/attendance/checkout', {
      method: 'POST',
      body: JSON.stringify({ childId, notes }),
    })
  },

  // Get currently checked-in children (for AddUpdateForm)
  async getCheckedIn(): Promise<{ children: CheckedInChild[] }> {
    return fetchWithAuth<{ children: CheckedInChild[] }>('/api/attendance/checked-in')
  },
}

export { ApiError }
